import { AzMapBoundsDTO, MapBoundsDTO, MapType, MapUIParameters, TableUIParameters } from './filter.models';
import { SortDirection } from '@soti/table';
import { ChartRecord } from 'core/services/dataRequest';
import { InsSelectionType } from '../../../../../../libs/table/src/lib/table/models/table.models';

export type ISmlParameterDisplayType = 'date' | 'dateRange' | 'dropdown' | 'string' | 'hour' | 'order' | 'quick';

export type ElementType = 'chart' | 'table' | 'map' | 'combobox' | 'list' | 'infotext' | 'summary';

export enum ElementTypeEnum {
  CHART = 'chart',
  TABLE = 'table',
  MAP = 'map',
  COMBOBOX = 'combobox',
  LIST = 'list',
  INFOTEXT = 'infotext',
  SUMMARY = 'summary',
}

export type ChartType =
  | 'bar'
  | 'cylinder'
  | 'column'
  | 'stacked_bar'
  | 'donut'
  | 'stacked_donut'
  | 'pie'
  | 'line'
  | 'timeline'
  | 'grouped-bar'
  | 'header'
  | 'combination';

export const enum ChartTypeEnum {
  BAR = 'bar',
  CYLINDER = 'cylinder',
  COLUMN = 'column',
  STACKED_BAR = 'stacked_bar',
  DONUT = 'donut',
  STACKED_DONUT = 'stacked_donut',
  PIE = 'pie',
  LINE = 'line',
  TIMELINE = 'timeline',
  GROUPED_BAR = 'grouped-bar',
  HEADER = 'header',
  GROUPED_LINE = 'grouped-line',
  COMBINATION = 'combination',
  GROUPED_COLUMN = 'grouped-column',
}

export const enum MostLeastEnum {
  MOST = 1,
  LEAST = -1,
}

export interface DashboardElement {
  id: string;
  elementType: ElementType;
  type?: string;
  parentChartId?: string;
  layout: string;
  filterSelected?: boolean;
  isFilterable?: boolean;
  name?: string;
  fullName: string | string[];
  uiSettings?: UiSetting[];
  uiParams?: MapUIParameters | TableUIParameters;
  baselineId?: string;
  pageId: string;
  record?: ChartRecord;
  filters?: TableFilters | MapFilters | ChartFilters;
  cardStyle?: string[];
}

export interface MapSideInfoPanelGroup {
  groupId: string;
  groupName: string;
  elements: MapSideInfoPanelElement[];
}

export interface MapSideInfoPanelElement {
  chartBaselineId: string;
  name: string;
}

export interface MapInfoPanelConfig {
  groups?: MapSideInfoPanelGroup[];
}

export interface MapFilters {
  filterBounds?: AzMapBoundsDTO;
  mapBounds?: MapBoundsDTO;
  mapZoom?: number;
  legends?: string[];
  selectedMarkers?: any[];
  mapType?: MapType;
  networkTypes?: string[]; //  2G, 3G, 4G
  lastSelectedMarkerId?: string;
  selectedMarkerIds?: string[];
  selectedMarkerHashLevel?: number[];
  carriers?: string[];
  hour?: number;
}

export interface ChartFilters {
  topN?: number;
  hour?: number | string;
  chargeGain?: number;
  chargeLevel?: number;
  shift?: number;
  history?: number;
  forecast?: number;
  timeInterval?: string;
  mostLeast?: number;
  showTop?: number;
  exStart?: string;
  exEnd?: string;
  excludeHours?: boolean;
  byDay?: number;
  store?: number;
}

export interface MapDashboardElement extends DashboardElement {
  mapInfoPanel?: MapInfoPanelConfig;
  filters: MapFilters;
  uiParams?: MapUIParameters;
}

export interface TooltipFormat {
  pastLabel?: string;
  label?: string;
  value?: string;
  labelArgs?: string[];
  valueArgs?: string[];
  pastLabelArgs?: string[];
  customChartColor?: boolean;
  dateTime?: string;
}

export interface MultiTooltipFormat {
  id?: string;
  value?: string;
  metricName?: string;
  subId?: string;
  timePointMetricId?: string;
}

export interface BaseChartConfig extends DashboardElement {
  fixedBelowMultiCharts?: boolean;
  reportSummary?: string;
  fullName: string;
  colors?: string[];
  fixedColors?: ChartsColorObject[];
  filter?: BaseCascadingFilterConfig;
  credits?: { enabled: boolean };
  multiTooltip?: MultiTooltipFormat[];
  tooltip?: TooltipFormat;
  tag?: {
    name: string;
    parameters?: string[];
  };
  selections?: any[]; // TODO
  dimension: BaseDimensionConfig;
  metric: BaseMetricConfig;
  isReportView?: boolean;
  dataLabelEnabled?: boolean;
  dataLabelDisabled?: boolean;
  toolTipDisabled?: boolean;
  legendDisabled?: boolean;
  chartHeight?: number;
  chartWidth?: number;
  disableTitle?: boolean;
  allowPointSelect?: boolean;
  legends?: any[];
  multiTimelineMetric?: any[];
  xAxis?: string;
  rotateLabels?: boolean;
  disableAnimation?: boolean;
  disable3D?: boolean;
  displayCondition?: DisplayCondition[];
  showSummary?: boolean;
  noDataIcon?: string;
  noDataText?: string;
  multiChart?: boolean;
  default?: boolean;
  alpha?: number;
  beta?: number;
  scrollableX?: boolean;
  dynamicLegend?: boolean;
  allowNegativeValue?: boolean;
  isMultipleMetrics?: boolean;
  reports?: boolean;
}

export interface DisplayCondition {
  operation?: string;
  filterIdx: string;
  filterValue?: any;
  filterLabel?: string;
}

export interface CardConfig extends DashboardElement {
  icon?: string;
  noDataIcon?: string;
  type?: string;
  iconTooltip?: string;
  chartHeight?: number;
  innerImage?: {
    image: string;
    class: string;
  };
  uiSettingsReadOnly?: boolean;
}

export interface TableFilters {
  filterBounds?: MapBoundsDTO;
  sortAttribute: string;
  sortDirection: SortDirection;
  pageSize?: number;
  pageSkip?: number;
}
export interface TableConfig extends DashboardElement {
  title: string;
  fullName: string;
  isFilterable: boolean;
  isReportView?: boolean;
  applicationTypes: string[];
  tableAttributes: TableAttribute[];
  name: string;
  uiParams?: TableUIParameters;
  filters?: TableFilters;
  childTableKey?: string;
  childTableTemplate?: string;
  selectionType?: InsSelectionType;
  selectionText?: string;
  selectionTextPlural?: string;
  rowActions?: RowAction[];
}

export interface RowAction {
  action: string;
  tooltip: string;
  icon: string;
}

export interface ChartsColorObject {
  label: string;
  color: string;
  metricId?: string;
}

export interface BarChartConfig extends BaseChartConfig {
  interval?: boolean;
  inverted?: boolean;
  type: ChartTypeEnum.BAR | ChartTypeEnum.COLUMN | ChartTypeEnum.CYLINDER | ChartTypeEnum.HEADER;
  colorByPoint?: boolean;
  integerOnly?: boolean;
  maxPointWidth?: number;
  formatTime?: boolean;
  uiProperties: {
    axis: {
      x: BaseAxisModel;
      y: BaseAxisModel;
    };
  };
  selections: BarSelectionConfig[];
  marginOverride?: number;
}

export interface CombinationChartConfig extends BaseChartConfig {
  stacking?: 'normal' | 'percent';
  childDimension: BaseDimensionConfig;
  integerOnly?: boolean;
  metrics?: Metrics[];
  timePointId?: string;
  yMax?: number;
  activateScroll?: number; // greater than this number will show scrollbar
}

export interface GroupedColumnChartConfig extends BaseChartConfig {
  integerOnly?: boolean;
  metrics?: Metrics[];
  timePointId?: string;
  yMax?: number;
  activateScroll?: number;
  maxScrollView?: number; // this will be the max number of columns on view when scroll is activated
  barWidth?: number;
  barDepth?: number;
}

export interface DonutChartConfig extends BaseChartConfig {
  type: ChartTypeEnum.DONUT;
  disableMouseEvent?: boolean;
  //selections: SliceSelectionsConfig[];
  pieSize?: number;
  innerSize?: number;
  hideDataLabelPercentage?: boolean;
}

export interface PieChartConfig extends BaseChartConfig {
  selections: SliceSelectionsConfig[];
}

/**
 * Summary chart item, used for data elements on Summary cards
 */
export interface SummaryChartItem {
  /** data key */
  key: string;

  /** label for value */
  label: string;

  /** optional icon - not currently implemented */
  icon?: string;

  /** optional color in r,g,b format */
  color?: string;

  /** optional adjusted style */
  style?: 'last_updated';

  /** additional items nested under this one */
  children?: SummaryChartItem[];

  /** tooltip for the container */
  tooltip?: string;

  /** tooltip for the container */
  childrenInSameContainer?: SummaryChartItem[];
}

export interface SummaryChartItemData {
  [key: string]: number | string;
}

export interface SummaryChartConfig extends DashboardElement {
  items: SummaryChartItem[];
}

export interface SliceSelectionsConfig {
  sliceLabel: string;
  sliceValues: any[];
}

export interface BarSelectionConfig {
  categoryLabel: string;
  categoryValues: any[];
  value?: any[];
}

export interface GroupedLineChartConfig extends BaseChartConfig {
  childDimension?: BaseDimensionConfig;
  multiChartConfig?: GroupedLineChartConfig[];
}

export interface Metrics {
  id: string;
  countId?: string;
  timeFormatValueId?: string;
  metricName?: string;
  type?: string;
  timePointMetricId?: string;
}

export interface LineChartConfig extends BaseChartConfig {
  formatDate?: boolean;
  formatTime?: boolean;
  dateFormatStr?: string;
  hourSwitch?: boolean;
  iconTooltip?: string;
  integerOnly?: boolean;
  metrics?: Metrics[];
  dataMapId?: string;
  pointTimeId?: string;
  legendClick?: boolean;
}
export interface TimelineChartConfig extends BaseChartConfig {
  integerOnly?: boolean;
  timelineColor?: string;
  timelineColors?: ChartsColorObject[];
  zoomType?: string;
  displayType?: string;
  xMinRange?: number;
  yMax?: number;
  plotBand?: boolean;
}

export interface StackedBarChartConfig extends BaseChartConfig {
  // TODO: Implement
  stacking?: 'normal' | 'percent';
  childDimension: BaseDimensionConfig;
  integerOnly?: boolean;
}

export interface StackedDonutChartConfig extends BaseChartConfig {
  type: ChartTypeEnum.STACKED_DONUT;
  childDimension: BaseDimensionConfig;
  pieSize?: number;
  colorMap?: { [key: string]: string };
  childColorMap?: { [key: string]: string[] };
  childTooltip?: TooltipFormat;
}

export interface GroupedBarChartConfig extends BaseChartConfig {
  type: ChartTypeEnum.GROUPED_BAR | ChartTypeEnum.CYLINDER;
  childDimension: BaseDimensionConfig;
}

export interface BaseDimensionConfig {
  dimensionId: string;
  label: string;
  labelDay?: string;
  noDataLabel?: string;
}

export interface BaseMetricConfig {
  metricFormat?: string;
  metricId: string;
  metricMax?: string;
  label: string;
  format?: string;
}

export type CascadingFilterType = 'simple' | 'mapped' | 'ranged' | 'expression';

export interface BaseCascadingFilterConfig {
  type?: CascadingFilterType;
  attribute: string;
  childAttribute?: string;
}

export interface RangedCascadingFilterConfig extends BaseCascadingFilterConfig {
  lowerBound: string;
  upperBound: string;
}

export interface BaseAxisModel {
  id: string;
  labelRotated: boolean;
  labelDegrees: number;
  label: string;
  field: string;
}

export interface TableUserConfig {
  selectedColumns: {
    [key: string]: boolean;
  };
}

export interface GenericBarChart {
  dimensionLabel: string;
  metricLabel: string;
  labelId?: string;
  points: Point[];
}

export interface CombinationChartBar {
  dimensionLabel: string;
  metricLabel: string;
  labelId?: string;
  points: Point[];
  type?: string;
}

export interface CombinationGroup {
  [key: string]: {
    values: CombinationPoint[];
    type: string;
  };
}

export interface CombinationPoint {
  y: number;
  timePoint?: string;
}

export interface GenericDonutChart {
  slices: BaseSlicesConfig[];
}

export interface GenericPieChart {
  slices: BaseSlicesConfig[];
}

export interface GenericLineChart {
  dimensionLabel: string;
  metricLabel: string;
  points: Point[];
  maxUnit?: string;
}
// export interface GenericTimelineChart {
//   metricLabel: string;
//   timelinePoints: TimelinePoint[];
// }

export interface GenericTable {
  rows: {}[];
}

export interface BaseSlicesConfig {
  label: string;
  value: number;
}

export interface Point {
  label: string;
  labelId?: string;
  value: string | number;
  type: PointTypeEnum;
  count?: number;
  timeFormatValue?: string;
  metricName?: string;
  isEntrance?: number;
  timePoint?: string;
}

export interface TimelinePoint {
  y: number;
  x: number;
  max?: number;
}

export interface GenericStackedBarChart {
  bars: GenericBarChart[];
  childDimensionMap: any;
}

export interface GenericCombinationChart {
  bars: GenericBarChart[];
  line: any;
}

export interface GenericGroupedColumnChart {
  bars: GenericBarChart[];
}

type PointTypeEnum = 'string' | 'number' | 'date';

export interface UiSetting {
  id?: string;
  type?: string;
  name?: string;
  value?: string | number;
  values?: string[] | number[];
  controlType: string;
  displayType?: ISmlParameterDisplayType;
  tooltip?: string;
  options?: DropdownItem[];
  action?: string;
  childOptions?: UiSetting[];
  isDynamic?: boolean;
  infoText?: string;
}

export interface DropdownItem {
  value: string | number;
  label: string | number;
}

export interface TableAttribute {
  id: string;
  label: string;
  renderer?: string;
  unit?: {
    baseUnitType: string;
    scaleFactor: number;
    unitLabel: string;
  };
  displayFormat?: string;
  primaryColumnSortId?: string;
  iconClass?: string;
  rendererParams?: any;
  shouldTranslate?: any;
}

export const BatteryStatusReportColumns = [
  'McDeviceName',
  'Model',
  'batteryHealthLabel',
  'dischargeRate',
  'batteryCapacityRounded',
];

export const AppUsageReportColumns = [
  'appName',
  'storageDisplayMB',
  'consumedTime',
  'visits',
  'downloads',

  'McDeviceName',
  'storageFreeAvg',
  'mostUsedApp',
  'appsInstalled',
  'appsUnopened',
];

export const PsDevicePageChartEnum = {
  psdDevSummary: 'psdDevSummary',
  psdModel: 'psdModel',
  psdWallUsage: 'psdWallUsage',
  psdDeviceUsage: 'psdDeviceUsage',
  psdAvgWallTime: 'psdAvgWallTime',
  psdAvgUtilTime: 'psdAvgUtilTime',
  psdAvgDeviceReturn: 'psdAvgDeviceReturn',
};

export const PsStorePageChartEnum = {
  pssPeakDevices: 'pssPeakDevices',
  pssPeakPct: 'pssPeakPct',
  pssLeastDevices: 'pssLeastDevices',
  pssLeastPct: 'pssLeastPct',
  pssAvgUtil: 'pssAvgUtil',
  pssAvgReturn: 'pssAvgReturn',
};

export const DataUsageReportColumns = ['McDeviceName', 'Model', 'totalUsageMB', 'topAppName', 'signalStrengthLabel'];

export function isTableDashboardElement(chart: DashboardElement): chart is TableConfig {
  return chart && chart.elementType === 'table';
}
export function isMapDashboardElement(chart: DashboardElement): chart is MapDashboardElement {
  return chart && ['map', 'location'].includes(chart.elementType);
}

export function isChartDashboardElement(chart: DashboardElement): chart is BaseChartConfig {
  return chart && chart.elementType === 'chart';
}

export function isComboBoxElement(chart: DashboardElement): chart is BaseChartConfig {
  return chart && chart.elementType === 'combobox';
}

export function isListDashboardElement(chart: DashboardElement) {
  return chart && chart.elementType === 'list';
}
